/**
 * 在线聊天本地缓存
 */
import {
    UserInfo,
    SessionItem,
    MessageItem,
    getUserInfo,
} from '@/api/chat/chatUser';
import SSIndexDB from '@/libs/SSIndexDB';
import { cloneDeep } from 'lodash';
import Vue from 'vue';
import { chatIndexDBName } from '@/config/constant';

// 5秒缓存时间（发起接口请求）
const LOCAL_CACHE_TIME = 1000 * 5;

const LOCAL_CACHE_LIMIT = 200;

const INDEX_DB_VERSION = 1;

const CHAT_USER_STORE_NAME = 'chat_user';

const CHAT_MESSAGE_STORE_NAME = 'chat_message';

interface storeNames {
    name: string;
    keyPath: string;
}

interface UserInfoCacheItem {
    key: string;
    data: UserInfo;
    updateTime: number;
}

interface MessageCacheItem {
    key: string;
    data: MessageItem[];
    updateTime: number;
}

interface SaveData {
    storeName: string;
    type: 'add' | 'edit';
    data: any;
    deleteData: any;
}

// eslint-disable-next-line import/prefer-default-export
export default class ChatLocalCache {
    constructor() {
        this.dbName = chatIndexDBName;
        this.chatDB = new SSIndexDB({
            dbName: this.dbName,
            dbVersion: this.dbVersion,
            storeNames: this.storeNames,
        });
        this.initIndexDB();
    }

    chatDB: any = null;

    dbName: string = '';

    dbVersion: number = INDEX_DB_VERSION;

    storeNames: storeNames[] = [
        { name: CHAT_USER_STORE_NAME, keyPath: 'key' },
        { name: CHAT_MESSAGE_STORE_NAME, keyPath: 'key' },
    ];

    userInfoCache: UserInfoCacheItem[] = [];

    messageCache: MessageCacheItem[] = [];

    // getUserInfo 请求
    xhrUserInfo: any = null;

    initIndexDB(isTryAgain = false) {
        this.chatDB.open().then(() => {
            /* 读取缓存数据 start */
            // 读取用户列表
            this.chatDB.getAllItems(CHAT_USER_STORE_NAME).then((res: any) => {
                this.userInfoCache = res.sort((a: any, b: any) => a.updateTime - b.updateTime);
            }).catch((err: any) => {
                console.error(err);
                let error = '';
                try {
                    error = err.toString();
                } catch (e) {
                    error = err.message || 'unknown error';
                }
                this.userInfoCache = [];
                Vue.prototype.$log('SS_INDEX_DB', 'GET_ALL_ITEMS_ERROR', { data: { store_name: CHAT_USER_STORE_NAME, error } });
            });

            // 读取消息列表
            this.chatDB.getAllItems(CHAT_MESSAGE_STORE_NAME).then((res: any) => {
                this.messageCache = res.sort((a: any, b: any) => a.updateTime - b.updateTime);
            }).catch((err: any) => {
                console.error(err);
                let error = '';
                try {
                    error = err.toString();
                } catch (e) {
                    error = err.message || 'unknown error';
                }
                this.messageCache = [];
                Vue.prototype.$log('SS_INDEX_DB', 'GET_ALL_ITEMS_ERROR', { data: { store_name: CHAT_MESSAGE_STORE_NAME, error } });
            });
            /* 读取缓存数据 end */

            // 日志打点
            if (isTryAgain) {
                Vue.prototype.$log('SS_INDEX_DB', 'REOPEN_SUCCESS', { data: { dbName: this.dbName, dbVersion: this.dbVersion } });
            }
        }).catch((err: any) => {
            this.userInfoCache = [];
            this.messageCache = [];
            const error = {
                error_code: err?.target?.errorCode,
                msg: '',
            };
            try {
                error.msg = err.toString();
            } catch (e) {
                error.msg = err.message || 'unknown error';
            }
            Vue.prototype.$log('SS_INDEX_DB', 'OPEN_ERROR', { data: { error, dbName: this.dbName, dbVersion: this.dbVersion, isTryAgain: Number(isTryAgain) } });

            // 重试
            if (!isTryAgain && error.msg.indexOf('Your browser does not support IndexedDB') === -1) {
                setTimeout(() => {
                    this.initIndexDB(true);
                }, 10000);
            }
        });
    }

    async saveDataToIndexDB({ storeName, type, data, deleteData }: SaveData) {
        try {
            if (this.chatDB.db) {
                if (type === 'edit') {
                    this.chatDB.updateItem(storeName, data);
                } else if (type === 'add') {
                    if (deleteData) {
                        await this.chatDB.deleteItem(storeName, deleteData.key);
                    }
                    await this.chatDB.addItem(storeName, data);
                }
            }
        } catch (err) {
            console.error(err);
            const error = {
                error_code: err?.target?.errorCode,
                msg: '',
            };
            try {
                error.msg = err.toString();
            } catch (e) {
                error.msg = err.message || 'unknown error';
            }
            Vue.prototype.$log('SS_INDEX_DB', 'SAVE_DATA_ERROR', { data: { storeName, type, error } });
        }
    }

    /**
     * 将用户信息存入缓存
     * @param chatUserId
     * @param data
     */
    setUserInfoCache(chatUserId: string, data: UserInfoCacheItem) {
        const index = this.userInfoCache.findIndex((userInfoItem) => userInfoItem.key === chatUserId);
        let delUserList: UserInfoCacheItem[] = [];
        let operationType: 'add' | 'edit' = 'add';
        if (index > -1) {
            this.userInfoCache[index] = data;
            operationType = 'edit';
        } else if (this.userInfoCache.length >= LOCAL_CACHE_LIMIT) {
            delUserList = this.userInfoCache.splice(0, 1);
            this.userInfoCache.push(data);
            operationType = 'add';
        } else {
            this.userInfoCache.push(data);
            operationType = 'add';
        }
        this.saveDataToIndexDB({
            storeName: CHAT_USER_STORE_NAME,
            type: operationType,
            data,
            deleteData: delUserList[0],
        }).finally(() => {});
    }

    setMessageCache(chatUserId: string, data: MessageCacheItem) {
        const index = this.messageCache.findIndex((messageItem) => messageItem.key === chatUserId);
        let delMessageList: MessageCacheItem[] = [];
        let operationType: 'add' | 'edit' = 'add';
        if (index > -1) {
            this.messageCache[index] = data;
            operationType = 'edit';
        } else if (this.messageCache.length >= LOCAL_CACHE_LIMIT) {
            delMessageList = this.messageCache.splice(0, 1);
            this.messageCache.push(data);
            operationType = 'add';
        } else {
            this.messageCache.push(data);
            operationType = 'add';
        }
        this.saveDataToIndexDB({
            storeName: CHAT_MESSAGE_STORE_NAME,
            type: operationType,
            data,
            deleteData: delMessageList[0],
        }).finally(() => {});
    }

    /**
     * 基于 session 组装用户信息
     * @param session
     */
    assemblyBasicUserInfo(session: SessionItem) {
        let labels: any[] = [];
        if (session.labels) {
            labels = cloneDeep(session.labels);
            labels.forEach((item: any) => {
                item.id = item.id ? item.id.toString() : '';
            });
        }
        const user: UserInfo = {
            chat_user_id: session.chat_user_id,
            name: session.title,
            remark_name: session.remark_name,
            avatar: session.avatar,
            sys_user_id: session.sys_user_id,
            nickname: '',
            // @ts-ignore
            channel: session.channel,
            channel_id: session.channel_id,
            channel_name: session.channel_name,
            robot_reply: session.robot_reply || '0',
            auto_translate: session.auto_translate,
            receive_translate: session.receive_translate,
            auto_translate_lang: session.auto_translate_lang,
            send_translate: session.send_translate,
            send_translate_lang: session.send_translate_lang,
            translation_preview: session.translation_preview,
            last_send_time: session.user_last_reply_time,
            // @ts-ignore
            user_last_reply_time: session.user_last_reply_time,
            labels,
            phone: session.phone,
            email: session.email,
            chat_session_code: session.chat_session_code,
            chat_type: session.type,
            channel_info: {},
            channel_config: {},
        };
        return user;
    }

    /**
     * 获取用户信息（接口请求）
     * @param id
     */
    fetchUserInfo(id: string) {
        return new Promise((resolve, reject) => {
            if (this.xhrUserInfo && this.xhrUserInfo.cancel) {
                this.xhrUserInfo.cancel();
            }
            this.xhrUserInfo = getUserInfo({ chat_user_id: id });
            this.xhrUserInfo.then((res: any) => {
                const data = res.data.data;
                if (data.channel === '2' && data.channel_info.content) {
                    data.channel_info.content = JSON.parse(data.channel_info.content as unknown as string);
                }
                if (!data.remark_name) {
                    data.remark_name = '';
                }
                if (!data.email) {
                    data.email = '';
                }
                if (!data.phone) {
                    data.phone = '';
                }
                if (!data.labels) {
                    data.labels = [];
                }
                if (!data.is_black) {
                    data.is_black = '';
                }
                if (!data.chat_session_code) {
                    Vue.prototype.$log('SESSION_LIST', 'SESSION_END_ACTIVE_USER', { data: { chat_user_id: data.chat_user_id } });
                }
                resolve(data);
            }).catch((e: any) => {
                reject(e);
            });
        });
    }

    /**
     * 获取用户信息
     * @param id
     * @param session
     * @param callback
     */
    getUserInfoById(id: string, session: SessionItem, callback: (data: UserInfo) => void) {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise((resolve, reject) => {
            const index = this.userInfoCache.findIndex((userInfoItem) => userInfoItem.key === id);
            if (index > -1) {
                const user = this.userInfoCache[index].data;
                callback(user);
                // 更新用户信息
                if (Date.now() - this.userInfoCache[index].updateTime > LOCAL_CACHE_TIME) {
                    // @ts-ignore
                    this.fetchUserInfo(id).then((data: UserInfo) => {
                        this.setUserInfoCache(id, {
                            key: id,
                            data,
                            updateTime: Date.now(),
                        });
                        resolve(data);
                    }).catch((e: any) => {
                        console.error(e);
                        reject(e);
                    });
                } else {
                    resolve(user);
                }
            } else {
                const user = this.assemblyBasicUserInfo(session);
                callback(user);
                // @ts-ignore
                this.fetchUserInfo(id).then((data: UserInfo) => {
                    this.setUserInfoCache(id, {
                        key: id,
                        data,
                        updateTime: Date.now(),
                    });
                    resolve(data);
                });
            }
        });
    }

    /**
     * 设置用户信息
     * @param id
     * @param data
     */
    setUserInfoById(id: string, data: UserInfo, isCurrentSession: boolean = true) {
        if (isCurrentSession) {
            this.setUserInfoCache(id, {
                key: id,
                data,
                updateTime: Date.now(),
            });
        } else {
            const index = this.userInfoCache.findIndex((userInfoItem) => userInfoItem.key === id);
            if (index > -1) {
                const userInfo = {
                    ...this.userInfoCache[index].data,
                    ...data,
                };
                this.setUserInfoCache(id, {
                    key: id,
                    data: userInfo,
                    updateTime: Date.now(),
                });
            }
        }
    }

    /**
     * 获取消息列表
     * @param id
     * @param callback
     */
    getMessageById(id: string, callback: (data: MessageItem[]) => void) {
        const index = this.messageCache.findIndex((messageItem) => messageItem.key === id);
        if (index > -1) {
            const messageList = this.messageCache[index].data;
            callback(messageList);
        } else {
            callback([]);
        }
    }

    /**
     * 设置消息列表
     * @param id
     * @param data
     */
    setMessageById(id: string, data: MessageItem[]) {
        this.setMessageCache(id, {
            key: id,
            data,
            updateTime: Date.now(),
        });
    }
}
